import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { CheckCircle2 } from 'lucide-react';
import { FormSelect } from '../components/FormSelect';
import { PartnerCTA } from '../components/PartnerCTA';
export function SupplierNetworkPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    country: '',
    website: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    supplierType: '',
    productCategories: [] as string[],
    capabilitySummary: '',
    regions: '',
    leadTimes: '',
    capacity: '',
    documentation: [] as string[],
    notes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // In a real app, this would send data to a backend
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'productCategories' | 'documentation') => {
    const {
      value,
      checked
    } = e.target;
    setFormData(prev => {
      const current = prev[field];
      if (checked) {
        return {
          ...prev,
          [field]: [...current, value]
        };
      } else {
        return {
          ...prev,
          [field]: current.filter(item => item !== value)
        };
      }
    });
  };
  const productCategoriesList = ['Bags and Backpacks', 'Clothing', 'Fire Retardants', 'Public Order / Control and Restraint', 'Tactical Vests', 'Training Equipment', 'Raw Materials / Textiles', 'Other'];
  const documentationList = ['Technical Datasheets', 'User Guides / Manuals', 'Test Reports / Certification', 'Quality Management Certificates (ISO)'];
  return <>
      <PageHero label="Partnerships" title="Supplier Network" subtitle="For manufacturers and supply partners supporting defence, police, prisons and medical procurement." data-id="element-1402" />

      <Breadcrumbs items={[{
      label: 'Supplier Network'
    }]} data-id="element-1403" />

      {/* Introduction */}
      <section className="bg-white py-20" data-id="element-1404">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1405">
          <div className="max-w-3xl" data-id="element-1406">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-1407">
              Who This Is For
            </h2>
            <p className="text-lg text-steel-600 leading-relaxed mb-8" data-id="element-1408">
              Chiltern Global maintains a rigorous supply chain to support
              critical public sector operations. We invite manufacturers, OEMs,
              producers, distributors, and specialist service providers to
              register their capabilities. This network supports our delivery of
              specification-led equipment across our core categories:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1409">
              {productCategoriesList.slice(0, 6).map(cat => <li key={cat} className="flex items-center gap-3 text-steel-700" data-id="element-1410">
                  <div className="w-1.5 h-1.5 bg-cg-orange-600 rounded-full" data-id="element-1411" />
                  {cat}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Requirements & Process */}
      <section className="bg-slate-50 py-20 border-y border-steel-200" data-id="element-1412">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1413">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-1414">
            {/* What We Look For */}
            <div data-id="element-1415">
              <h2 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-1416">
                What Chiltern Global Looks For
              </h2>
              <div className="bg-white p-8 border border-steel-200 border-l-4 border-l-cg-orange-600 rounded-sm space-y-6" data-id="element-1417">
                <div data-id="element-1418">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-1419">
                    Specification-Led Capability
                  </h3>
                  <p className="text-steel-600 text-sm" data-id="element-1420">
                    Ability to manufacture or supply strictly to technical
                    specifications and drawings.
                  </p>
                </div>
                <div data-id="element-1421">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-1422">
                    Documentation Readiness
                  </h3>
                  <p className="text-steel-600 text-sm" data-id="element-1423">
                    Availability of technical datasheets, test reports, and
                    compliance documentation where applicable.
                  </p>
                </div>
                <div data-id="element-1424">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-1425">
                    Consistency & Quality
                  </h3>
                  <p className="text-steel-600 text-sm" data-id="element-1426">
                    Demonstrable quality management processes to ensure
                    repeatability and reliability.
                  </p>
                </div>
                <div data-id="element-1427">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-1428">
                    Transparency
                  </h3>
                  <p className="text-steel-600 text-sm" data-id="element-1429">
                    Clear communication regarding lead times, capacity, and
                    origin of manufacture.
                  </p>
                </div>
              </div>
            </div>

            {/* How Onboarding Works */}
            <div data-id="element-1430">
              <h2 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-1431">
                How Onboarding Works
              </h2>
              <div className="space-y-6" data-id="element-1432">
                {['Submit supplier details via the form below', 'Portfolio and capability review by our procurement team', 'Documentation and compliance review', 'Commercial and logistics alignment', 'Supplier profile created for future sourcing briefs'].map((step, index) => <div key={index} className="flex gap-4" data-id="element-1433">
                    <div className="flex-shrink-0 w-8 h-8 bg-navy-900 text-white font-bold flex items-center justify-center rounded-sm text-sm" data-id="element-1434">
                      {index + 1}
                    </div>
                    <p className="text-steel-700 pt-1" data-id="element-1435">{step}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section id="submit-details" className="bg-white py-24" data-id="element-1436">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-1437">
          <div className="text-center mb-12" data-id="element-1438">
            <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1439">
              Submit Supplier Details
            </h2>
            <p className="text-steel-600" data-id="element-1440">
              Please provide accurate information to help us route your details
              to the correct category manager.
            </p>
          </div>

          {!submitted ? <form onSubmit={handleSubmit} className="bg-slate-50 p-8 md:p-12 border border-steel-200 rounded-sm space-y-10" data-id="element-1441">
              {/* Company Details */}
              <div data-id="element-1442">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 border-b border-steel-200 pb-2" data-id="element-1443">
                  Company Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1444">
                  <div className="md:col-span-2" data-id="element-1445">
                    <label htmlFor="companyName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1446">
                      Company Name *
                    </label>
                    <input type="text" id="companyName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.companyName} onChange={handleChange} data-id="element-1447" />
                  </div>
                  <div data-id="element-1448">
                    <label htmlFor="country" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1449">
                      Country *
                    </label>
                    <input type="text" id="country" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.country} onChange={handleChange} data-id="element-1450" />
                  </div>
                  <div data-id="element-1451">
                    <label htmlFor="website" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1452">
                      Website *
                    </label>
                    <input type="url" id="website" required placeholder="https://" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.website} onChange={handleChange} data-id="element-1453" />
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div data-id="element-1454">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 border-b border-steel-200 pb-2" data-id="element-1455">
                  Primary Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1456">
                  <div data-id="element-1457">
                    <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1458">
                      First Name *
                    </label>
                    <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-1459" />
                  </div>
                  <div data-id="element-1460">
                    <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1461">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-1462" />
                  </div>
                  <div data-id="element-1463">
                    <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1464">
                      Work Email *
                    </label>
                    <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-1465" />
                  </div>
                  <div data-id="element-1466">
                    <label htmlFor="role" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1467">
                      Role / Title *
                    </label>
                    <input type="text" id="role" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.role} onChange={handleChange} data-id="element-1468" />
                  </div>
                </div>
              </div>

              {/* Capability Details */}
              <div data-id="element-1469">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 border-b border-steel-200 pb-2" data-id="element-1470">
                  Capability Profile
                </h3>
                <div className="space-y-6" data-id="element-1471">
                  <div data-id="element-1472">
                    <FormSelect id="supplierType" label="Supplier Type" required value={formData.supplierType} onChange={handleChange} data-id="element-1473">
                      <option value="" data-id="element-1474">Select Type</option>
                      <option value="Manufacturer / OEM" data-id="element-1475">
                        Manufacturer / OEM
                      </option>
                      <option value="Producer" data-id="element-1476">Producer</option>
                      <option value="Distributor / Stockist" data-id="element-1477">
                        Distributor / Stockist
                      </option>
                      <option value="Service Provider" data-id="element-1478">Service Provider</option>
                      <option value="Other" data-id="element-1479">Other</option>
                    </FormSelect>
                  </div>

                  <div data-id="element-1480">
                    <span className="block text-sm font-bold text-navy-900 mb-3" data-id="element-1481">
                      Product Categories Offered *
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1482">
                      {productCategoriesList.map(cat => <label key={cat} className="flex items-center gap-3 cursor-pointer" data-id="element-1483">
                          <input type="checkbox" value={cat} checked={formData.productCategories.includes(cat)} onChange={e => handleCheckboxChange(e, 'productCategories')} className="w-4 h-4 text-cg-orange-600 border-steel-300 rounded focus:ring-cg-orange-500" data-id="element-1484" />
                          <span className="text-sm text-steel-700" data-id="element-1485">{cat}</span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-1486">
                    <label htmlFor="capabilitySummary" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1487">
                      Capability Summary *
                    </label>
                    <textarea id="capabilitySummary" required rows={5} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" placeholder="Please describe your core manufacturing capabilities, specialisms, and key product lines..." value={formData.capabilitySummary} onChange={handleChange} data-id="element-1488" />
                  </div>
                </div>
              </div>

              {/* Additional Information (Optional) */}
              <div data-id="element-1489">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 border-b border-steel-200 pb-2" data-id="element-1490">
                  Additional Information (Optional)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1491">
                  <div data-id="element-1492">
                    <label htmlFor="regions" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1493">
                      Regions Supplied
                    </label>
                    <input type="text" id="regions" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" placeholder="e.g. UK, Europe, Global" value={formData.regions} onChange={handleChange} data-id="element-1494" />
                  </div>
                  <div data-id="element-1495">
                    <label htmlFor="leadTimes" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1496">
                      Typical Lead Times
                    </label>
                    <input type="text" id="leadTimes" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.leadTimes} onChange={handleChange} data-id="element-1497" />
                  </div>
                </div>

                <div className="mb-6" data-id="element-1498">
                  <span className="block text-sm font-bold text-navy-900 mb-3" data-id="element-1499">
                    Documentation Available
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1500">
                    {documentationList.map(doc => <label key={doc} className="flex items-center gap-3 cursor-pointer" data-id="element-1501">
                        <input type="checkbox" value={doc} checked={formData.documentation.includes(doc)} onChange={e => handleCheckboxChange(e, 'documentation')} className="w-4 h-4 text-cg-orange-600 border-steel-300 rounded focus:ring-cg-orange-500" data-id="element-1502" />
                        <span className="text-sm text-steel-700" data-id="element-1503">{doc}</span>
                      </label>)}
                  </div>
                </div>

                <div data-id="element-1504">
                  <label htmlFor="notes" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-1505">
                    Additional Notes
                  </label>
                  <textarea id="notes" rows={3} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.notes} onChange={handleChange} data-id="element-1506" />
                </div>
              </div>

              <div data-id="element-1507">
                <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors" data-id="element-1508">
                  Submit Supplier Details
                </button>
                <p className="text-xs text-steel-500 text-center mt-4 leading-relaxed" data-id="element-1509">
                  By submitting this form, you acknowledge that Chiltern Global
                  will process your information for the purpose of reviewing
                  your submission and administering related communications. For
                  further details, please review our{' '}
                  <Link to="/privacy-policy" className="underline hover:text-cg-orange-600" data-id="element-1510">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms-of-service" className="underline hover:text-cg-orange-600" data-id="element-1511">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </form> : <div className="bg-green-50 border border-green-200 p-12 rounded-sm text-center" data-id="element-1512">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" data-id="element-1513" />
              <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-1514">
                Thank you. Your submission has been received.
              </h3>
              <p className="text-steel-700 mb-8 max-w-md mx-auto" data-id="element-1515">
                Your details have been logged in our supplier database. Our
                procurement team will review your capabilities and contact you
                if there is an alignment with current or future sourcing
                requirements.
              </p>
              <Link to="/" className="inline-block px-8 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-800 transition-colors" data-id="element-1516">
                Return Home
              </Link>
            </div>}
        </div>
      </section>

      <PartnerCTA data-id="element-1517" />
    </>;
}